import { useSyncExternalStore } from 'react';
import { getExtensionStatus, ExtensionStatus, EXTENSION_STATUS_EVENT } from '../services/extensionBridge';

// One copy for the page, so every component that shows it agrees and the
// extension is asked once rather than once per component.
let status: ExtensionStatus | null = null;
const listeners = new Set<() => void>();

const set = (next: ExtensionStatus) => {
  status = next;
  listeners.forEach((listener) => listener());
};

const onStatus = (event: Event) => set((event as CustomEvent<ExtensionStatus>).detail);

/** Ask the extension again, e.g. after the user installs it. */
export function refreshExtensionStatus(): Promise<void> {
  return getExtensionStatus().then(set);
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    window.addEventListener(EXTENSION_STATUS_EVENT, onStatus);
    // Changes published while nothing was listening were missed.
    refreshExtensionStatus();
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener(EXTENSION_STATUS_EVENT, onStatus);
  };
}

const getSnapshot = (): ExtensionStatus | null => status;

/**
 * Whether the browser extension is installed and who it is signed in as; null
 * until it has answered. Follows connects and sign-outs made from the dashboard.
 */
export function useExtensionStatus(): ExtensionStatus | null {
  return useSyncExternalStore(subscribe, getSnapshot);
}
